import { EVENTS } from '@archetype-themes/utils/events'

class StickyAddToCart extends HTMLElement {
  constructor() {
    super()

    this.stickyForm = this.querySelector('form')
    this.hiddenInput = this.querySelector('input[name="id"]')
    this.addButton = this.querySelector('.btn')
    this.addButtonText = this.querySelector('.btn > span')
    this.productId = this.dataset.productId
    this.sectionId = this.dataset.sectionId

    this.handleVariantChange = this.handleVariantChange.bind(this)
    this.checkVisibility = this.checkVisibility.bind(this)
    this.handleCartDrawerEvents = this.handleCartDrawerEvents.bind(this)
    
    this.isCartDrawerOpen = false
  }

  connectedCallback() {
    this.abortController = new AbortController()

    // Listen for variant changes on the main product form
    document.addEventListener(
      `${EVENTS.variantChange}:${this.sectionId}:${this.productId}`,
      this.handleVariantChange,
      { signal: this.abortController.signal }
    )

    // Setup scroll listener with throttling for performance
    this.lastKnownScrollPosition = 0
    this.ticking = false

    window.addEventListener('scroll', () => {
      this.lastKnownScrollPosition = window.scrollY

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.checkVisibility()
          this.ticking = false
        })

        this.ticking = true
      }
    }, { signal: this.abortController.signal })

    // Handle form submission
    if (this.stickyForm) {
      this.stickyForm.addEventListener('submit', this.handleSubmit.bind(this), {
        signal: this.abortController.signal
      })
    }
    
    // Listen for cart drawer open/close events
    document.addEventListener('drawer:open', this.handleCartDrawerEvents, { 
      signal: this.abortController.signal 
    })
    document.addEventListener('drawer:close', this.handleCartDrawerEvents, { 
      signal: this.abortController.signal 
    })

    // Initial check
    this.checkVisibility()
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  handleCartDrawerEvents(event) {
    // Check if it's specifically the cart drawer that's being toggled
    const drawer = event.target.closest('header-cart-drawer');
    if (!drawer) return;
    
    this.isCartDrawerOpen = event.type === 'drawer:open';
    this.checkVisibility();
  }

  checkVisibility() {
    // If cart drawer is open, always hide sticky add-to-cart
    if (this.isCartDrawerOpen) {
      this.classList.remove('visible');
      return;
    }
    
    // Get positions
    const mainAddToCartBtn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`)
    if (!mainAddToCartBtn) return

    const mainBtnRect = mainAddToCartBtn.getBoundingClientRect()
    const windowHeight = window.innerHeight

    // Show sticky bar when main add to cart button is out of viewport
    if (mainBtnRect.bottom < 0 || mainBtnRect.top > windowHeight) {
      this.classList.add('visible')
    } else {
      this.classList.remove('visible')
    }
  }

  handleVariantChange({ detail }) {
    const { variant } = detail

    if (!variant) {
      this.toggleAddButton(true, this.getLocales().unavailable)
      return
    }

    // Update the hidden input with the new variant id
    if (this.hiddenInput) {
      this.hiddenInput.value = variant.id
    }

    // Update the button state
    const isAvailable = variant.available
    this.toggleAddButton(!isAvailable, isAvailable ? '' : this.getLocales().soldOut)

    // Update the price
    const priceElement = this.querySelector('.sticky-add-to-cart__price')
    if (priceElement && variant.price) {
      const formattedPrice = this.formatMoney(variant.price)
      priceElement.textContent = formattedPrice

      if (variant.compare_at_price > variant.price) {
        const comparePrice = this.formatMoney(variant.compare_at_price)
        priceElement.innerHTML = `<span class="sale-price">${formattedPrice}</span> <s>${comparePrice}</s>`
      } else {
        priceElement.textContent = formattedPrice
      }
    }
  }

  toggleAddButton(disable = true, text) {
    if (!this.addButton) return

    if (disable) {
      this.addButton.setAttribute('disabled', 'disabled')
      if (text && this.addButtonText) this.addButtonText.textContent = text
    } else {
      this.addButton.removeAttribute('disabled')
      if (this.addButtonText) {
        this.addButtonText.textContent =
          this.dataset.template !== 'preorder'
            ? this.getLocales().addToCart
            : this.getLocales().preOrder
      }
    }
  }

  getLocales() {
    this.locales = this.locales || JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.locales
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents)
    }

    const value = cents / 100
    return `$${value.toFixed(2)}`
  }

  async handleSubmit(event) {
    if (this.dataset.cartType == 'page') return

    event.preventDefault()
    this.disableAddToCartButton()
    this.classList.remove('visible'); // Hide sticky bar immediately

    try {
      const responseJson = await this.addVariantToCart()

      this.dispatchEvent(
        new CustomEvent(EVENTS.ajaxProductAdded, {
          bubbles: true,
          detail: {
            product: responseJson,
            addToCartBtn: this.addButton
          }
        })
      )
    } catch (error) {
      console.error('Error adding product to cart:', error)
    } finally {
      this.enableAddToCartButton()
    }
  }

  async addVariantToCart() {
    const formData = this.getFormData()

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })

    if (!response.ok) {
      throw await response.json()
    }

    return response.json()
  }

  getFormData() {
    const formData = new FormData(this.stickyForm)
    formData.set('sections_url', `${window.Shopify.routes.root}variants/${this.hiddenInput.value}`)
    formData.set('sections', 'cart-ajax')
    return formData
  }

  enableAddToCartButton() {
    if (!this.addButton) return

    this.addButton.removeAttribute('aria-busy')
    this.addButton.classList.remove('btn--loading')
  }

  disableAddToCartButton() {
    if (!this.addButton) return

    this.addButton.setAttribute('aria-busy', 'true')
    this.addButton.classList.add('btn--loading')
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart)
